import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Eye } from "lucide-react";

interface EmailPreviewStepProps {
  html: string;
  subject: string;
  recipientEmail: string;
  onBack: () => void;
  onConfirmSend: () => void;
  loading?: boolean;
  sendButtonLabel?: string;
  sendButtonVariant?: "default" | "destructive";
  sendButtonClassName?: string;
  loadingIcon?: React.ReactNode;
}

const EmailPreviewStep = ({
  html,
  subject,
  recipientEmail,
  onBack,
  onConfirmSend,
  loading,
  sendButtonLabel = "Jetzt senden",
  sendButtonVariant = "default",
  sendButtonClassName,
  loadingIcon,
}: EmailPreviewStepProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
      }
    }
  }, [html]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
        <Eye className="h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium text-foreground">E-Mail-Vorschau</p>
          <p><strong>An:</strong> {recipientEmail}</p>
          <p><strong>Betreff:</strong> {subject}</p>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <iframe
          ref={iframeRef}
          className="w-full border-0"
          style={{ height: "400px" }}
          title="E-Mail-Vorschau"
          sandbox="allow-same-origin"
        />
      </div>

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück bearbeiten
        </Button>
        <Button
          onClick={onConfirmSend}
          disabled={loading}
          variant={sendButtonVariant}
          className={sendButtonClassName}
        >
          {loading ? (
            loadingIcon || <Send className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {sendButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default EmailPreviewStep;
